import { useEffect } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import RareBoxLogo from "../../Logos/RareBoxLogo";

// Public, unauthenticated layout for the privacy policy and terms pages.
// Deliberately not wrapped in DefaultOpenLayout: these pages are read by
// outsiders (including Google's OAuth reviewer) and must not show the app Navbar.
// Palette and surface treatment match Auth/Login/Login.js.
const LegalPage = ({ pill, title, effective, children }) => {
	useEffect(() => {
		const previous = document.title;
		document.title = `${title} — Rarebox`;
		return () => {
			document.title = previous;
		};
	}, [title]);

	return (
		<Box
			sx={{
				minHeight: "100dvh",
				px: 2,
				py: { xs: 3, md: 5 },
				background:
					"radial-gradient(circle at top left, rgba(255,255,255,0.96), rgba(240,238,233,0.92) 42%, rgba(229,226,220,0.9) 100%)",
			}}>
			<Box
				component="main"
				sx={{
					maxWidth: 820,
					mx: "auto",
					p: { xs: 3, sm: 4, md: 6 },
					borderRadius: "10px",
					border: "1px solid rgba(17, 17, 17, 0.08)",
					backgroundColor: "rgba(255,255,255,0.72)",
					backdropFilter: "blur(18px)",
					boxShadow: "0 24px 80px rgba(34, 34, 34, 0.12)",
					color: "var(--color-text-primary)",
					lineHeight: 1.7,
					"& h2": {
						m: "40px 0 12px",
						fontSize: "1.2rem",
						fontWeight: 600,
						letterSpacing: "-0.02em",
					},
					"& p, & li": { color: "var(--color-text-secondary)" },
					"& ul": { pl: 2.75 },
					"& li": { mb: 1 },
					"& a": { color: "var(--color-text-primary)", fontWeight: 600 },
					"& .callout": {
						m: "24px 0",
						p: "20px 24px",
						borderLeft: "3px solid #111111",
						borderRadius: "4px",
						backgroundColor: "rgba(255,255,255,0.6)",
					},
					"& .callout p": { m: 0 },
				}}>
				<Box sx={{ width: 210, mb: 3.5, "& svg": { display: "block", width: "100%", height: "auto" } }}>
					<RareBoxLogo />
				</Box>
				<Box
					component="span"
					sx={{
						display: "inline-block",
						px: 1.75,
						py: 0.75,
						borderRadius: "999px",
						backgroundColor: "rgba(17, 17, 17, 0.06)",
						color: "var(--color-text-secondary)",
						fontSize: "0.78rem",
						fontWeight: 600,
						letterSpacing: "0.08em",
						textTransform: "uppercase",
					}}>
					{pill}
				</Box>
				<Typography
					component="h1"
					sx={{
						m: "20px 0 8px",
						fontSize: { xs: "1.9rem", sm: "2.25rem" },
						fontWeight: 600,
						lineHeight: 1.05,
						letterSpacing: "-0.04em",
					}}>
					{title}
				</Typography>
				<Typography sx={{ color: "var(--color-text-light)", fontSize: "0.9rem" }}>Effective {effective}</Typography>
				{children}
			</Box>
			<Box
				component="footer"
				sx={{
					maxWidth: 820,
					mx: "auto",
					mt: 3.5,
					px: 1,
					color: "var(--color-text-light)",
					fontSize: "0.88rem",
					"& a": { color: "var(--color-text-secondary)", fontWeight: 600, mr: 2.25, textDecoration: "none" },
				}}>
				<Link to="/">Home</Link>
				<Link to="/privacy-policy">Privacy Policy</Link>
				<Link to="/terms-of-service">Terms of Service</Link>
				<span>© 2026 Radhamani Textiles Pvt. Ltd.</span>
			</Box>
		</Box>
	);
};

export default LegalPage;
